import { FC, Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import AppLayout from './layouts/AppLayout';
import AdminRoute from './components/auth/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ModuleLayout from './components/navigation/ModuleLayout';
import InsufficientBalanceListener from './components/llm/InsufficientBalanceListener';
import DialogHost from './components/common/DialogHost';
import SubscriptionUpgradeListener from './components/billing/SubscriptionUpgradeListener';
import apiClient from './api/client';
import { getToken, clearToken, clearRefreshToken } from './api/auth';

// ── Auth & Layout ──────────────────────────────────────
// 10-05：WelcomePage 从**静态** import 改成 lazy。原因（真机实测）：它是落地页，
// 带 three.js 3D 背景（three.module 720KB）+ MoonlitRipple（112KB），静态 import 会
// 让这两个 chunk 进首屏 modulepreload —— **每次启动都无条件下载+编译 800KB+**，
// 哪怕已登录用户直接进 /app、一辈子看不到落地页。改成按需后首屏少这 800KB。
// 代价：游客首次落到 /welcome 会先看到骨架屏一瞬间（原先是想「即时」）。
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
import { useAuth } from '@/hooks/useAuth';
import { useAutoSnapshot } from '@/hooks/useAutoSnapshot';
import { useSettings } from '@/store/settings';
import { useSystemFeatures } from '@/hooks/useSystemFeatures';
import { getActiveProvider } from '@/api/llm';

// ── Learn (public, no auth) ────────────────────────
const LearnHubPage = lazy(() => import('./pages/learn/LearnHubPage'));
const LearnArticlePage = lazy(() => import('./pages/learn/LearnArticlePage'));

// ── 只读分享公开视图（public, no auth）──────────────
const ShareView = lazy(() => import('./pages/share/ShareView'));

// ── Lazy-loaded pages ───────────────────────────────
const Dashboard = lazy(() => import('./pages/Dashboard'));
const WorkbenchPage = lazy(() => import('./pages/workbench/WorkbenchPage'));
const FolderPage = lazy(() => import('./pages/folders/FolderPage'));
const TenantMembersPage = lazy(() => import('./pages/tenant/MembersPage'));
const IngestPage = lazy(() => import('./pages/ingest/IngestPage'));
const GraphLayout = lazy(() => import('./pages/graph/GraphLayout'));
const GraphNetworkPage = lazy(() => import('./pages/graph/GraphNetworkPage'));
const GraphQueryPage = lazy(() => import('./pages/graph/GraphQueryPage'));
const GraphPathPage = lazy(() => import('./pages/graph/GraphPathPage'));
const GraphReportPage = lazy(() => import('./pages/graph/GraphReportPage'));
const GraphBridgesPage = lazy(() => import('./pages/graph/GraphBridgesPage'));
const GraphTagsPage = lazy(() => import('./pages/graph/GraphTagsPage'));
const WikiPage = lazy(() => import('./pages/graph/WikiPage'));
const GraphTimelinePage = lazy(() => import('./pages/graph/GraphTimelinePage'));
// 3D 星系视图：知识网络默认形态（/graph 与菜单「知识网络」落点），2D 在 /graph/network
const GraphGalaxyPage = lazy(() => import('./pages/graph/GraphGalaxyPage'));
const AttentionDashboardPage = lazy(() => import('./pages/attention/AttentionDashboardPage'));
const AttentionDeepWorkPage = lazy(() => import('./pages/attention/AttentionDeepWorkPage'));
const AttentionBudgetPage = lazy(() => import('./pages/attention/AttentionBudgetPage'));
const AttentionGuardianPage = lazy(() => import('./pages/attention/AttentionGuardianPage'));
const AttentionRationPage = lazy(() => import('./pages/attention/AttentionRationPage'));
const AttentionStatsPage = lazy(() => import('./pages/attention/AttentionStatsPage'));
const KnowledgeDetail = lazy(() => import('./pages/knowledge/KnowledgeDetail'));
const AllKnowledgePage = lazy(() => import('./pages/knowledge/AllKnowledgePage'));
const NetworkKnowledgePage = lazy(() => import('./pages/knowledge/NetworkKnowledgePage'));
const PersonalKnowledgePage = lazy(() => import('./pages/knowledge/PersonalKnowledgePage'));
const VerificationCenterPage = lazy(() => import('./pages/knowledge/VerificationCenterPage'));
const SourceTraceabilityPage = lazy(() => import('./pages/knowledge/SourceTraceabilityPage'));
const CounterEvidenceWallPage = lazy(() => import('./pages/knowledge/CounterEvidenceWallPage'));
const CredibilityMapPage = lazy(() => import('./pages/knowledge/CredibilityMapPage'));
const TimelinessMonitorPage = lazy(() => import('./pages/knowledge/TimelinessMonitorPage'));
const KnowledgeStatsPage = lazy(() => import('./pages/knowledge/KnowledgeStatsPage'));
const KnowledgeCreatePage = lazy(() => import('./pages/knowledge/KnowledgeCreatePage'));
const CapsuleListPage = lazy(() => import('./pages/capsules/CapsuleListPage'));
const CapsuleCreate = lazy(() => import('./pages/capsules/CapsuleCreate'));
const CapsuleDetail = lazy(() => import('./pages/capsules/CapsuleDetail'));
const CapsulePlazaPage = lazy(() => import('./pages/capsules/CapsulePlazaPage'));
const CapsuleSchedulePage = lazy(() => import('./pages/capsules/CapsuleSchedulePage'));
const CapsuleStatsPage = lazy(() => import('./pages/capsules/CapsuleStatsPage'));
const CapsuleDialoguePage = lazy(() => import('./pages/capsules/CapsuleDialoguePage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const NotesPage = lazy(() => import('./pages/ingest/NotesPage'));
const StickyNotesPage = lazy(() => import('./pages/ingest/StickyNotesPage'));
const TagsPage = lazy(() => import('./pages/ingest/TagsPage'));
const NoteDetail = lazy(() => import('./pages/ingest/NoteDetail'));
const ClipperPage = lazy(() => import('./pages/ingest/ClipperPage'));
const BookmarksPage = lazy(() => import('./pages/ingest/BookmarksPage'));
const EmailPage = lazy(() => import('./pages/ingest/EmailPage'));
const BatchImportPage = lazy(() => import('./pages/ingest/BatchImportPage'));
const RssPage = lazy(() => import('./pages/ingest/RssPage'));
const SocialPage = lazy(() => import('./pages/ingest/SocialPage'));
const ReadLaterPage = lazy(() => import('./pages/ingest/ReadLaterPage'));
const DocumentLibraryPage = lazy(() => import('./pages/ingest/DocumentLibraryPage'));
const CognitivePage = lazy(() => import('./pages/cognitive/CognitivePage'));
const FingerprintPage = lazy(() => import('./pages/cognitive/FingerprintPage'));
const BiasPage = lazy(() => import('./pages/cognitive/BiasPage'));
const CognitiveConflictPage = lazy(() => import('./pages/cognitive/CognitiveConflictPage'));
const DecisionAuditPage = lazy(() => import('./pages/cognitive/DecisionAuditPage'));
const FutureSimulationPage = lazy(() => import('./pages/cognitive/FutureSimulationPage'));
const CognitiveChallengePage = lazy(() => import('./pages/cognitive/CognitiveChallengePage'));
const CognitiveWeeklyReportPage = lazy(() => import('./pages/cognitive/CognitiveWeeklyReportPage'));
const BusinessPlanPage = lazy(() => import('./pages/BusinessPlanPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const TopupPage = lazy(() => import('./pages/TopupPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const EmergencePage = lazy(() => import('./pages/emergence/EmergencePage'));
const AssociatePage = lazy(() => import('./pages/emergence/AssociatePage'));
const CollisionPage = lazy(() => import('./pages/emergence/CollisionPage'));
const HybridPage = lazy(() => import('./pages/emergence/HybridPage'));
const CounterfactualPage = lazy(() => import('./pages/emergence/CounterfactualPage'));
const SourcePoolPage = lazy(() => import('./pages/emergence/SourcePoolPage'));
const CanvasPage = lazy(() => import('./pages/emergence/CanvasPage'));
const IdeaLibraryPage = lazy(() => import('./pages/emergence/IdeaLibraryPage'));
const SearchPage = lazy(() => import('./pages/search/SearchPage'));
const ChatHistoryPage = lazy(() => import('./pages/chat/ChatHistoryPage'));
const CommunityPage = lazy(() => import('./pages/community/CommunityPage'));
const GuidePage = lazy(() => import('./pages/community/GuidePage'));

// Pipeline
const PipelineOverviewPage = lazy(() => import('./pages/pipeline/PipelineOverviewPage'));
const RawMaterialsPage = lazy(() => import('./pages/pipeline/RawMaterialsPage'));
import CardsPage from './pages/pipeline/CardsPage';
const ExtractPage = lazy(() => import('./pages/pipeline/ExtractPage'));
const PipelineCollisionPage = lazy(() => import('./pages/pipeline/CollisionPage'));
const AnnotatePage = lazy(() => import('./pages/pipeline/AnnotatePage'));

// Social Brain
const JianghuOverviewPage = lazy(() => import('./pages/jianghu/JianghuOverviewPage'));
const DailyReviewPage = lazy(() => import('./pages/jianghu/DailyReviewPage'));
const KnowledgeHealthPage = lazy(() => import('./pages/jianghu/KnowledgeHealthPage'));
const PracticeRecordsPage = lazy(() => import('./pages/jianghu/PracticeRecordsPage'));
const EvolutionTrackPage = lazy(() => import('./pages/jianghu/EvolutionTrackPage'));
const InvocationTrackPage = lazy(() => import('./pages/jianghu/InvocationTrackPage'));
const AiContextPage = lazy(() => import('./pages/socialBrain/AiContextPage'));
const CognitivePotentialPage = lazy(() => import('./pages/socialBrain/CognitivePotentialPage'));
const ExperimenterMindsetPage = lazy(() => import('./pages/shared/ExperimenterMindsetPage'));

// Embodied Cognition
const EmbodiedOverviewPage = lazy(() => import('./pages/embodiedCognition/EmbodiedOverviewPage'));
const DepthCheckPage = lazy(() => import('./pages/embodiedCognition/DepthCheckPage'));
const TrueEvolutionPage = lazy(() => import('./pages/embodiedCognition/TrueEvolutionPage'));
const MoodLocationPage = lazy(() => import('./pages/embodiedCognition/MoodLocationPage'));

// Admin
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminModels = lazy(() => import('./pages/admin/AdminModels'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'));
const AdminSupport = lazy(() => import('./pages/admin/AdminSupport'));
const AdminTenants = lazy(() => import('./pages/admin/AdminTenants'));
const AdminDailyReports = lazy(() => import('./pages/admin/AdminDailyReports'));

// ── Skeleton fallback ───────────────────────────────
const PageSkeleton: FC = () => (
  <div className="min-h-[60vh] animate-pulse space-y-6 p-6">
    <div className="h-8 bg-bg-tertiary rounded-lg w-1/3" />
    <div className="h-4 bg-bg-tertiary rounded-lg w-2/3" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="h-32 bg-bg-tertiary rounded-xl" />
      <div className="h-32 bg-bg-tertiary rounded-xl" />
      <div className="h-32 bg-bg-tertiary rounded-xl" />
    </div>
    <div className="h-48 bg-bg-tertiary rounded-xl" />
  </div>
);

const AuthGuard: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isLoggedIn, userError } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const landingPage = useSettings((s) => s.landingPage);
  const { data: sysFeatures } = useSystemFeatures();
  // 掉线兜底（08-20）：token 还在但 user 查询因 429/5xx/网络抖动失败时，
  // 不得踢回欢迎页（观感=被强制退出）——显示繁忙页并自动重试。
  // 只有 401/403（token 真失效）才走原有未登录逻辑。
  const userErrorStatus = (userError as any)?.status ?? (userError as any)?.response?.status ?? null;
  const transientUserFailure = Boolean(
    !isLoggedIn && !isLoading && getToken() && userError &&
    userErrorStatus !== 401 && userErrorStatus !== 403
  );
  // 游客演示探测：后端游客中间件对匿名 GET 注入演示账号（官网「打开即玩」依赖）。
  // 探测 200 → 放行游客只读浏览；401（自托管无演示账号）→ 维持回欢迎页。
  const [guestDemo, setGuestDemo] = useState<boolean | null>(null);
  useEffect(() => {
    if (isLoggedIn || guestDemo !== null) return;
    const probe = () => apiClient.get('/api/v1/users/me', { skipAuthRefresh: true } as Record<string, unknown>);
    probe()
      .then(() => setGuestDemo(true))
      .catch(async () => {
        // 陈旧 token（过期/账号已删）：游客中间件只给无凭证请求注入演示号，
        // 带死 token 探测必 401——清掉后纯匿名重试一次，而不是被踢回欢迎页
        if (getToken()) {
          clearToken();
          clearRefreshToken();
          try {
            await probe();
            setGuestDemo(true);
            return;
          } catch { /* 匿名也不通 → 无演示模式 */ }
        }
        setGuestDemo(false);
      });
  }, [isLoggedIn, guestDemo]);

  // 探测失败（guestDemo=false，多为后端未就绪/瞬时网络问题）后整 session 不重试
  // 会把用户永久挡在欢迎页——60s 后把状态打回 null 触发一次重探，仅一次避免死循环
  const guestRetryDoneRef = useRef(false);
  useEffect(() => {
    if (isLoggedIn || guestDemo !== false || guestRetryDoneRef.current) return;
    guestRetryDoneRef.current = true;
    const timer = setTimeout(() => setGuestDemo(null), 60000);
    return () => clearTimeout(timer);
  }, [isLoggedIn, guestDemo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="w-8 h-8 border-2 border-info border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 瞬时故障（限流/抖动）：会话保留，给重试出口，不进未登录流程
  if (transientUserFailure) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg-primary gap-4">
        <AlertTriangle className="w-10 h-10 text-warning" />
        <div className="text-text-primary font-medium">服务繁忙，正在重试…</div>
        <div className="text-sm text-text-secondary">你的登录状态没有丢失，稍等片刻或手动重试</div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['user'] })}
          className="btn-secondary text-sm"
        >
          立即重试
        </button>
      </div>
    );
  }

  // Redirect logged-in users from welcome page to their landing page
  // （工作台立项：默认首页=工作台，设置里可切回仪表盘；模块开关关→仪表盘）
  if (isLoggedIn && (location.pathname === '/' || location.pathname === '/welcome')) {
    const workbenchOn = sysFeatures?.modules?.workbench !== false; // 键缺失=开（存量部署无此键）
    return <Navigate to={landingPage === 'workbench' && workbenchOn ? '/workbench' : '/app'} replace />;
  }

  // 未登录：欢迎页放行；其余路径有游客演示则只读放行，否则回欢迎页
  if (!isLoggedIn) {
    if (location.pathname === '/' || location.pathname === '/welcome') {
      return <WelcomePage />;
    }
    if (guestDemo === null) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-primary">
          <div className="w-8 h-8 border-2 border-info border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (guestDemo) {
      return <>{children}</>;
    }
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
};

// 未匹配路由兜底（404 页面）。nginx 仅对已知顶层路由回退 SPA，
// 真正未知地址由服务端返回 404；这里处理另一界面版本的专属路由，
// 并动态写入 noindex，避免搜索引擎收录。
// 多数未匹配路径其实是另一界面版本的专属路由，因此保留「切到经典版打开」入口；
// 不再 2.5 秒自动跳转（自动跳转既不告诉用户真相，也让爬虫看到和首页相同的内容）。
const RouteFallback: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setUiMode = useSettings((s) => s.setUiMode);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = '页面不存在（404）· Molore';
    // index.html 自带 <meta name="robots" content="index,follow">：直接改写它的 content，
    // 避免页面上同时存在两个 robots meta 互相冲突；离开时恢复原值
    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const prevRobots = robotsMeta?.getAttribute('content') ?? null;
    robotsMeta?.setAttribute('content', 'noindex,nofollow');
    return () => {
      document.title = prevTitle;
      if (robotsMeta) {
        if (prevRobots !== null) robotsMeta.setAttribute('content', prevRobots);
        else robotsMeta.removeAttribute('content');
      }
    };
  }, [location.pathname]);

  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-4">
      <div className="text-5xl font-bold text-text-muted tracking-widest">404</div>
      <div className="text-text-primary font-semibold">页面不存在，或在当前界面版本不可用</div>
      <div className="text-sm text-text-secondary max-w-md">
        <span className="font-mono">{location.pathname}</span> 没有对应的页面。
        如果它是经典版专属功能，可以切换界面版本后再打开。
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            setUiMode('classic');
            navigate(location.pathname, { replace: true });
          }}
          className="btn-primary text-xs px-3 py-1.5"
        >
          切到经典版打开
        </button>
        <button
          onClick={() => navigate('/app', { replace: true })}
          className="text-xs px-3 py-1.5 rounded-[2px] border border-border-color text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors"
        >
          返回首页
        </button>
      </div>
    </div>
  );
};

// 默认首页分流：workbench=工作台（默认）/ dashboard=旧仪表盘（设置里可切回）
// module_workbench_enabled=false 时强制回仪表盘（管理端模块开关优先于个人设置）
const HomeLanding: FC = () => {
  const landingPage = useSettings((s) => s.landingPage);
  const { data: features } = useSystemFeatures();
  const workbenchOn = features?.modules?.workbench !== false; // 键缺失=开（存量部署无此键）
  return landingPage === 'workbench' && workbenchOn ? <WorkbenchPage /> : <Dashboard />;
};

const App: FC = () => {
  const { isLoggedIn } = useAuth();
  // 每日自动加密快照（仅桌面端、已登录、已解锁时静默执行，详见 hooks/useAutoSnapshot）
  useAutoSnapshot();
  const syncActiveProvider = useSettings((state) => state.syncActiveProvider);
  // 界面版本：classic=经典版完整功能（默认），simple=简化版五动作
  const uiMode = useSettings((state) => state.uiMode);
  const isClassic = uiMode === 'classic';

  useEffect(() => {
    if (!isLoggedIn) return;
    getActiveProvider()
      .then((data) => {
        // 强刷后用户可能已在本 GET 在途期间点了新模型：旧值不得打回新选择
        // （症状：强刷后第一次选型被强跳回默认模型）。避让窗口与 status 回读同口径。
        const { lastActiveWriteAt, activeWritePending } = useSettings.getState();
        if (activeWritePending || Date.now() - lastActiveWriteAt < 5000) return;
        if (data.provider && data.model) {
          syncActiveProvider(data.provider, data.model);
        }
      })
      .catch(() => {
        // Ignore: user might not have set an active provider yet
      });
  }, [isLoggedIn, syncActiveProvider]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        {/* DialogHost 必须包裹 Routes：Provider 在 DialogHost 内部，
            当兄弟节点挂时全站 useConfirm 拿到默认「永远取消」（09-23 实捕） */}
        <DialogHost>
        <InsufficientBalanceListener />
        <SubscriptionUpgradeListener />
        <Routes>
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/learn" element={<LearnHubPage />} />
          <Route path="/learn/:slug" element={<LearnArticlePage />} />
          <Route path="/share/:token" element={<ShareView />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route
            path="/*"
            element={
              <AuthGuard>
                <AppLayout />
              </AuthGuard>
            }
          >
            <Route index element={<HomeLanding />} />
            <Route path="app" element={<Dashboard />} />
            <Route path="workbench" element={<WorkbenchPage />} />

            {/* Ingest */}
            <Route path="ingest" element={<ModuleLayout menuId="ingest" />}>
              <Route index element={<IngestPage />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="notes/new" element={<NoteDetail />} />
              <Route path="notes/:id" element={<NoteDetail />} />
              <Route path="sticky-notes" element={<StickyNotesPage />} />
              <Route path="tags" element={<TagsPage />} />
              <Route path="clipper" element={<ClipperPage />} />
              <Route path="bookmarks" element={<BookmarksPage />} />
              <Route path="email" element={<EmailPage />} />
              <Route path="batch-import" element={<BatchImportPage />} />
              <Route path="rss" element={<RssPage />} />
              <Route path="social" element={<SocialPage />} />
              <Route path="read-later" element={<ReadLaterPage />} />
              <Route path="documents" element={<DocumentLibraryPage />} />
              {/* 时间轴已从图谱搬到采集模块：全量内容批次回顾，不再是图谱节点视图 */}
              <Route path="timeline" element={<GraphTimelinePage />} />
              {!isClassic && <Route path="sources" element={<SourcePoolPage />} />}
              {isClassic && <Route path="sources" element={<Navigate to="/emergence/sources" replace />} />}
            </Route>

            {/* Graph：默认落 3D 星系物理图（用户拍板 3D 第一展示）；/graph/network 仍是 2D 显式地址 */}
            <Route path="graph" element={<GraphLayout />}>
              <Route index element={<Navigate to="/graph/galaxy" replace />} />
              <Route path="network" element={<GraphNetworkPage />} />
              {/* 简化版 AI 问答属「问答」桶，挂 ask 布局（见下方独立路由） */}
              {isClassic && <Route path="query" element={<GraphQueryPage />} />}
              {/* 双模式只隐入口不隐路由：直接 URL 在简化版也可访问（README 约定） */}
              <Route path="path" element={<GraphPathPage />} />
              <Route path="report" element={<GraphReportPage />} />
              <Route path="bridges" element={<GraphBridgesPage />} />
              <Route path="tags" element={<GraphTagsPage />} />
              {/* 百科（LLM Wiki）：摄入时编译的主题条目页 */}
              <Route path="wiki" element={<WikiPage />} />
              {/* 旧路径重定向：时间轴已搬到 /ingest/timeline（见上注释） */}
              <Route path="timeline" element={<Navigate to="/ingest/timeline" replace />} />
              {/* 3D 星系视图：知识网络默认形态（2D⇄3D 互切在两页工具栏） */}
              <Route path="galaxy" element={<GraphGalaxyPage />} />
            </Route>
            {/* 简化版 AI 问答：语义属「问答」，挂 ask 二级菜单，与顶部高亮桶一致 */}
            {!isClassic && (
              <Route path="graph/query" element={<ModuleLayout menuId="ask" showOverview={false} />}>
                <Route index element={<GraphQueryPage />} />
              </Route>
            )}

            <Route path="search" element={<SearchPage />} />
            {/* 文件夹内容页：双界面版本共用（树在全局侧边栏，两版都可见） */}
            <Route path="folders/:id" element={<FolderPage />} />
            {/* 团队成员管理：双界面版本通用（作用于当前激活空间） */}
            <Route path="tenant/members" element={<TenantMembersPage />} />
            {/* 对话历史：双界面版本可用（问答的答案沉淀，语义属「问答」，见 navigation.ts 菜单注释） */}
            {/* 对话历史：挂模块布局以显示二级菜单（简化版在「问答」、经典版在「图谱」） */}
            {isClassic ? (
              <Route path="chat" element={<ModuleLayout menuId="graph" showOverview={false} />}>
                <Route index element={<ChatHistoryPage />} />
              </Route>
            ) : (
              <Route path="chat" element={<ModuleLayout menuId="ask" showOverview={false} />}>
                <Route index element={<ChatHistoryPage />} />
              </Route>
            )}
            <Route path="community" element={<CommunityPage />} />
            <Route path="community/guide" element={<GuidePage />} />
            {isClassic ? (
              <Route path="daily-review" element={<DailyReviewPage />} />
            ) : (
              <>
                {/* 旧路径重定向到「每日」模块 */}
                <Route path="daily-review" element={<Navigate to="/daily" replace />} />
                <Route path="daily" element={<ModuleLayout menuId="daily" />}>
                  <Route index element={<DailyReviewPage />} />
                  <Route path="knowledge-health" element={<KnowledgeHealthPage />} />
                  <Route path="weekly-report" element={<CognitiveWeeklyReportPage />} />
                  <Route path="challenge" element={<CognitiveChallengePage />} />
                  <Route path="practice-records" element={<PracticeRecordsPage />} />
                  <Route path="evolution-track" element={<EvolutionTrackPage />} />
                  <Route path="invocation-track" element={<InvocationTrackPage />} />
                </Route>
              </>
            )}

            {/* Attention（经典版） */}
            {isClassic && (
              <Route path="attention" element={<ModuleLayout menuId="attention" />}>
                <Route index element={<AttentionDashboardPage />} />
                <Route path="dashboard" element={<AttentionDashboardPage />} />
                <Route path="deep-work" element={<AttentionDeepWorkPage />} />
                <Route path="budget" element={<AttentionBudgetPage />} />
                <Route path="guardian" element={<AttentionGuardianPage />} />
                <Route path="ration" element={<AttentionRationPage />} />
                <Route path="stats" element={<AttentionStatsPage />} />
              </Route>
            )}

            {/* Capsules */}
            {isClassic ? (
              <Route path="capsules" element={<ModuleLayout menuId="capsules" showOverview={false} />}>
                <Route index element={<Navigate to="my" replace />} />
                <Route path="my" element={<CapsuleListPage />} />
                <Route path="create" element={<CapsuleCreate />} />
                <Route path="dialogue" element={<CapsuleDialoguePage />} />
                <Route path="plaza" element={<CapsulePlazaPage />} />
                <Route path="schedule" element={<CapsuleSchedulePage />} />
                <Route path="stats" element={<CapsuleStatsPage />} />
                <Route path=":id" element={<CapsuleDetail />} />
              </Route>
            ) : (
              <Route path="capsules" element={<ModuleLayout menuId="ask" showOverview={false} />}>
                <Route index element={<Navigate to="my" replace />} />
                <Route path="my" element={<CapsuleListPage />} />
                <Route path="create" element={<CapsuleCreate />} />
                {/* 经典版专属子页：显式兜底，避免被 :id 吞掉显示空详情 */}
                <Route path="dialogue" element={<RouteFallback />} />
                <Route path="plaza" element={<RouteFallback />} />
                <Route path="schedule" element={<RouteFallback />} />
                <Route path="stats" element={<RouteFallback />} />
                <Route path=":id" element={<CapsuleDetail />} />
              </Route>
            )}

            {/* Knowledge */}
            {isClassic ? (
              <Route path="knowledge" element={<ModuleLayout menuId="knowledge" showOverview={false} />}>
                <Route index element={<Navigate to="network" replace />} />
                <Route path="network" element={<NetworkKnowledgePage />} />
                <Route path="personal" element={<PersonalKnowledgePage />} />
                <Route path="all" element={<AllKnowledgePage />} />
                <Route path="verify" element={<VerificationCenterPage />} />
                <Route path="sources" element={<SourceTraceabilityPage />} />
                <Route path="counter" element={<CounterEvidenceWallPage />} />
                <Route path="credibility" element={<CredibilityMapPage />} />
                <Route path="timeliness" element={<TimelinessMonitorPage />} />
                <Route path="stats" element={<KnowledgeStatsPage />} />
                <Route path="create" element={<KnowledgeCreatePage />} />
                <Route path=":id" element={<KnowledgeDetail />} />
              </Route>
            ) : (
              <Route path="knowledge" element={<ModuleLayout menuId="ask" showOverview={false} />}>
                <Route index element={<Navigate to="network" replace />} />
                <Route path="network" element={<NetworkKnowledgePage />} />
                <Route path="personal" element={<PersonalKnowledgePage />} />
                <Route path="all" element={<AllKnowledgePage />} />
                <Route path="verify" element={<VerificationCenterPage />} />
                <Route path="sources" element={<SourceTraceabilityPage />} />
                <Route path="counter" element={<CounterEvidenceWallPage />} />
                <Route path="credibility" element={<CredibilityMapPage />} />
                <Route path="create" element={<KnowledgeCreatePage />} />
                {/* 经典版专属子页：显式兜底，避免被 :id 吞掉显示空详情 */}
                <Route path="timeliness" element={<RouteFallback />} />
                <Route path="stats" element={<RouteFallback />} />
                <Route path=":id" element={<KnowledgeDetail />} />
              </Route>
            )}

            {/* Pipeline */}
            <Route path="pipeline" element={<ModuleLayout menuId="pipeline" />}>
              <Route index element={<PipelineOverviewPage />} />
              <Route path="raw" element={<RawMaterialsPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="extract" element={<ExtractPage />} />
              <Route path="collision" element={<PipelineCollisionPage />} />
              <Route path="annotate" element={<AnnotatePage />} />
            </Route>

            {/* Social Brain（经典版） */}
            {isClassic && (
              <Route path="social-brain" element={<ModuleLayout menuId="social-brain" />}>
                <Route index element={<JianghuOverviewPage />} />
                <Route path="ai-context" element={<AiContextPage />} />
                <Route path="cognitive-potential" element={<CognitivePotentialPage />} />
                <Route path="experimenter" element={<ExperimenterMindsetPage />} />
                <Route path="daily-review" element={<DailyReviewPage />} />
                <Route path="knowledge-health" element={<KnowledgeHealthPage />} />
                <Route path="practice-records" element={<PracticeRecordsPage />} />
                <Route path="evolution-track" element={<EvolutionTrackPage />} />
                <Route path="invocation-track" element={<InvocationTrackPage />} />
              </Route>
            )}

            {/* Embodied Cognition（经典版） */}
            {isClassic && (
              <Route path="embodied-cognition" element={<ModuleLayout menuId="embodied-cognition" />}>
                <Route index element={<EmbodiedOverviewPage />} />
                <Route path="depth-check" element={<DepthCheckPage />} />
                <Route path="true-evolution" element={<TrueEvolutionPage />} />
                <Route path="mood-location" element={<MoodLocationPage />} />
              </Route>
            )}

            {/* Emergence（经典版） */}
            {isClassic && (
              <Route path="emergence" element={<ModuleLayout menuId="emergence" />}>
                <Route index element={<EmergencePage />} />
                <Route path="sources" element={<SourcePoolPage />} />
                <Route path="associate" element={<AssociatePage />} />
                <Route path="collision" element={<CollisionPage />} />
                <Route path="hybrid" element={<HybridPage />} />
                <Route path="counterfactual" element={<CounterfactualPage />} />
                <Route path="canvas" element={<CanvasPage />} />
                <Route path="library" element={<IdeaLibraryPage />} />
              </Route>
            )}
            {/* 简化版只露涌现画布（归「自动理好」桶，见 navigation.ts）；其余创意工具经典版专属 */}
            {!isClassic && (
              <Route path="emergence/canvas" element={<ModuleLayout menuId="pipeline" />}>
                <Route index element={<CanvasPage />} />
              </Route>
            )}

            {/* Cognitive（经典版） */}
            {isClassic && (
              <Route path="cognitive" element={<ModuleLayout menuId="cognitive" />}>
                <Route index element={<CognitivePage />} />
                <Route path="fingerprint" element={<FingerprintPage />} />
                <Route path="bias" element={<BiasPage />} />
                <Route path="conflict" element={<CognitiveConflictPage />} />
                <Route path="audit" element={<DecisionAuditPage />} />
                <Route path="simulate" element={<FutureSimulationPage />} />
                <Route path="challenge" element={<CognitiveChallengePage />} />
                <Route path="weekly-report" element={<CognitiveWeeklyReportPage />} />
              </Route>
            )}

            {/* Settings */}
            <Route path="settings" element={<ModuleLayout menuId="settings" showOverview={false} />}>
              <Route index element={<Navigate to="account" replace />} />
              <Route path="account" element={<SettingsPage />} />
              <Route path="ai" element={<SettingsPage />} />
              <Route path="byok" element={<SettingsPage />} />
              <Route path="privacy" element={<SettingsPage />} />
              <Route path="sync" element={<SettingsPage />} />
              <Route path="desktop" element={<SettingsPage />} />
              <Route path="storage" element={<SettingsPage />} />
              <Route path="appearance" element={<SettingsPage />} />
              <Route path="data" element={<SettingsPage />} />
              <Route path="plugins" element={<SettingsPage />} />
              <Route path="recycle" element={<SettingsPage />} />
            </Route>

            {/* 会员/充值：全员功能（Pro 与按量对所有用户开放），双界面版本可用 */}
            <Route path="payment" element={<PaymentPage />} />
            <Route path="topup" element={<TopupPage />} />

            {/* 计费/会员（商用功能，视为经典版专属） */}
            {isClassic && (
              <>
                <Route path="business-plan" element={<BusinessPlanPage />} />
                <Route path="billing" element={<BillingPage />} />
              </>
            )}

            {/* 未匹配路由（含另一模式的专属路由）：提示后可一键切经典版 */}
            <Route path="*" element={<RouteFallback />} />
          </Route>
          {/* Admin routes（后台管理不参与界面版本门控） */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="models" element={<AdminModels />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="reports" element={<AdminDailyReports />} />
          </Route>
        </Routes>
        </DialogHost>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
